// Floating Plivo browser-SDK call widget. Mounted by ActiveCallProvider via a
// portal so it persists across drawer/tab switches. Props are stable for the
// widget's lifetime — the parent re-mounts via key={callLogId} when a new call
// starts. We deliberately use an empty deps array on the init effect.

import React, { useEffect, useRef, useState } from "react";
import {
  PhoneOff, Mic, MicOff, Loader2, PhoneCall, FileText, Send, X,
} from "lucide-react";
import toast from "react-hot-toast";
import { fetchPlivoBrowserCredentials, useUpdateCallNote } from "../../hooks/useLeads";

function formatDuration(seconds) {
  const m = Math.floor(seconds / 60).toString().padStart(2, "0");
  const s = (seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

const CALL_END_STATUSES = ["completed", "failed", "canceled", "rejected"];

const STATUS_LABEL = {
  connecting: "Connecting…",
  ringing: "Ringing…",
  "in-progress": "In call",
  completed: "Call ended",
  failed: "Call failed",
  canceled: "Cancelled",
  rejected: "Rejected",
};

export default function PlivoBrowserCallWidget({
  leadId,
  callLogId,
  to,
  leadTitle,
  testMode = false,
  onClose,
  onStatusChange,
}) {
  const [status, setStatus] = useState("connecting");
  const [muted, setMuted] = useState(false);
  const [duration, setDuration] = useState(0);

  const [showNotes, setShowNotes] = useState(false);
  const [noteTitle, setNoteTitle] = useState("");
  const [noteDescription, setNoteDescription] = useState("");
  const [isSavingNote, setIsSavingNote] = useState(false);

  const phoneRef = useRef(null);
  const timerRef = useRef(null);
  const startedAtRef = useRef(null);
  const autoCloseRef = useRef(null);
  const showNotesRef = useRef(false);
  useEffect(() => { showNotesRef.current = showNotes; }, [showNotes]);

  // The Plivo SDK can fire onCallFailed / onLoginFailed (and sometimes a
  // terminate event right after) more than once for a single failed attempt —
  // each previously stacked its own toast. Guard so a given call session shows
  // at most one error toast, and pin a stable toast id so any stray repeat
  // replaces rather than stacks.
  const errorToastShownRef = useRef(false);
  const callErrorToast = (message) => {
    if (errorToastShownRef.current) return;
    errorToastShownRef.current = true;
    toast.error(message, { id: "plivo-call-error" });
  };

  // Bubble status up so ActiveCallProvider can decide whether a new call
  // attempt should silently replace this widget (terminal status) or block
  // with the "already in progress" toast (live status).
  useEffect(() => {
    onStatusChange?.(status);
  }, [status, onStatusChange]);

  const updateCallNote = useUpdateCallNote(leadId);

  const scheduleAutoClose = (delayMs = 5000) => {
    if (autoCloseRef.current) clearTimeout(autoCloseRef.current);
    autoCloseRef.current = setTimeout(() => {
      if (!showNotesRef.current) onClose?.();
    }, delayMs);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const PlivoMod = await import("plivo-browser-sdk");
        const Plivo = PlivoMod.Plivo || PlivoMod.default;

        const creds = await fetchPlivoBrowserCredentials(leadId);
        if (cancelled) return;

        const phone = new Plivo({
          debug: "WARN",
          permOnClick: true,
          codecs: ["OPUS", "PCMU"],
          enableIPV6: false,
          audioConstraints: { optional: [{ googAutoGainControl: false }] },
          dscp: true,
          enableTracking: true,
        });
        phoneRef.current = phone;

        phone.client.on("onLogin", () => {
          if (cancelled) return;
          const extraHeaders = { "X-PH-callLogId": String(callLogId || "") };
          if (testMode) {
            const targetUsername = (typeof to === "string" && to.startsWith("sip:"))
              ? to.replace(/^sip:/, "").split("@")[0]
              : to;
            extraHeaders["X-PH-targetEndpoint"] = String(targetUsername || "");
          }
          phone.client.call(to, extraHeaders);
        });

        phone.client.on("onLoginFailed", (cause) => {
          callErrorToast(`Plivo login failed: ${cause}`);
          setStatus("failed");
        });

        phone.client.on("onCallFailed", (cause) => {
          callErrorToast(`Call failed: ${cause}`);
          setStatus("failed");
          if (timerRef.current) clearInterval(timerRef.current);
          scheduleAutoClose(3000);
        });

        phone.client.on("onCallAnswered", () => {
          setStatus("in-progress");
          startedAtRef.current = Date.now();
          timerRef.current = setInterval(() => {
            setDuration(Math.floor((Date.now() - startedAtRef.current) / 1000));
          }, 1000);
        });

        phone.client.on("onOutgoingCallRinging", () => setStatus("ringing"));
        phone.client.on("onCalling", () => setStatus("connecting"));

        const handleEnd = () => {
          setStatus((prev) => (CALL_END_STATUSES.includes(prev) ? prev : "completed"));
          if (timerRef.current) clearInterval(timerRef.current);
          scheduleAutoClose(5000);
        };
        phone.client.on("onCallTerminated", handleEnd);
        phone.client.on("onCallEnded", handleEnd);
        phone.client.on("onCallCancelled", handleEnd);
        phone.client.on("onCallRejected", handleEnd);
        phone.client.on("onMediaDisconnected", () => {
          if (timerRef.current) clearInterval(timerRef.current);
          handleEnd();
        });

        phone.client.login(creds.username, creds.password);
      } catch (err) {
        callErrorToast(err.message || "Could not start Plivo browser call");
        setStatus("failed");
      }
    })();

    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
      if (autoCloseRef.current) clearTimeout(autoCloseRef.current);
      // Only hangup if there's actually a live call session — otherwise the
      // SDK logs "No call session exists to hangup" warnings during unmount
      // (e.g. user closed the widget right after onCallFailed fired).
      try {
        if (phoneRef.current?.client?.callSession) phoneRef.current.client.hangup();
      } catch {}
      try { phoneRef.current?.client?.logout(); } catch {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleMute = () => {
    const phone = phoneRef.current;
    if (!phone) return;
    const next = !muted;
    try {
      if (next) phone.client.mute(); else phone.client.unmute();
      setMuted(next);
    } catch {}
  };

  const hangup = () => {
    // Guard the SDK call against "no call session" warnings — the button can
    // be clicked after the remote side already ended the call (small race
    // between onCallTerminated firing and the auto-close timer hiding the UI).
    try {
      if (phoneRef.current?.client?.callSession) phoneRef.current.client.hangup();
    } catch {}
    setStatus("completed");
  };

  const handleSaveNote = async () => {
    if (!noteDescription.trim()) {
      toast.error("Please add notes about the call");
      return;
    }
    setIsSavingNote(true);
    try {
      await updateCallNote.mutateAsync({
        callId: callLogId,
        title: noteTitle.trim() || undefined,
        description: noteDescription.trim(),
      });
      toast.success("Call notes saved to timeline");
      onClose?.();
    } catch {
      toast.error("Failed to save call notes");
    } finally {
      setIsSavingNote(false);
    }
  };

  const labelForStatus = STATUS_LABEL[status] || status;
  const callEnded = CALL_END_STATUSES.includes(status);
  const headerSubtitle = testMode ? `Test endpoint: ${to}` : to;

  const shell = {
    position: "fixed", bottom: 24, right: 24, zIndex: 9999, width: 320,
    background: "var(--color-bg-surface)",
    border: "1px solid var(--color-border-subtle)",
    borderRadius: 8,
    boxShadow: "0 24px 60px rgba(0,0,0,0.22)",
    overflow: "hidden",
    color: "var(--color-text-primary)",
  };
  const header = {
    padding: "14px 16px",
    background: "linear-gradient(135deg, var(--crm-primary), var(--crm-primary-strong, var(--crm-primary)))",
    color: "#fff",
  };
  const eyebrow = {
    display: "flex", alignItems: "center", gap: 6,
    fontSize: 10, fontWeight: 600, opacity: 0.85,
  };
  const circleBtn = (bg, color, opacity = 1) => ({
    width: 48, height: 48, borderRadius: 9999, border: "none",
    display: "flex", alignItems: "center", justifyContent: "center",
    background: bg, color, cursor: "pointer", opacity,
    transition: "background-color 120ms ease",
  });
  const fieldLabel = {
    display: "block", fontSize: 10, fontWeight: 600,
    color: "var(--color-text-secondary)", marginBottom: 4,
  };
  const inputBase = {
    width: "100%", padding: "8px 10px", borderRadius: 8,
    border: "1px solid var(--color-border-subtle)",
    background: "var(--color-bg-base)", color: "var(--color-text-primary)",
    fontSize: 13, outline: "none", boxSizing: "border-box",
  };

  return (
    <div style={shell} role="dialog" aria-label="Active call">
      {/* Plivo SDK pipes remote audio into this element. Without it the SDK
          logs "No speaker element found" and the caller hears nothing on
          some browsers. id is the SDK's default selector. */}
      <audio id="plivo-remote-audio" autoPlay style={{ display: "none" }} />
      <div style={header}>
        <div style={eyebrow}>
          <PhoneCall size={12} />
          Plivo browser call{testMode ? " · TEST" : ""}
        </div>
        <div style={{ marginTop: 4, fontWeight: 600, fontSize: 15, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {leadTitle || "Lead"}
        </div>
        <div style={{ fontSize: 12, opacity: 0.9, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {headerSubtitle}
        </div>
      </div>

      {!showNotes && (
        <div style={{ padding: "20px 16px", textAlign: "center" }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: "var(--color-text-secondary)", }}>
            {labelForStatus}
          </div>
          <div style={{ marginTop: 8, fontSize: 28, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontWeight: 600 }}>
            {status === "connecting" || status === "ringing"
              ? <Loader2 className="animate-spin" size={28} style={{ display: "inline-block" }} />
              : formatDuration(duration)}
          </div>

          <div style={{ marginTop: 20, display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
            <button
              onClick={toggleMute}
              disabled={status !== "in-progress"}
              title={muted ? "Unmute" : "Mute"}
              style={circleBtn(
                muted ? "var(--crm-danger-soft)" : "var(--color-bg-muted)",
                muted ? "var(--crm-danger)" : "var(--color-text-primary)",
                status !== "in-progress" ? 0.4 : 1,
              )}
            >
              {muted ? <MicOff size={18} /> : <Mic size={18} />}
            </button>

            {callEnded ? (
              <>
                <button
                  onClick={() => {
                    if (autoCloseRef.current) clearTimeout(autoCloseRef.current);
                    setShowNotes(true);
                  }}
                  style={{
                    height: 48, padding: "0 16px", borderRadius: 9999, border: "none",
                    background: "var(--crm-primary)", color: "black",
                    fontSize: 13, fontWeight: 600, cursor: "pointer",
                    display: "inline-flex", alignItems: "center", gap: 6,
                  }}
                >
                  <FileText size={14} /> Add notes
                </button>
                <button
                  onClick={onClose}
                  style={{
                    height: 48, padding: "0 14px", borderRadius: 9999, border: "none",
                    background: "var(--color-bg-muted)", color: "var(--color-text-primary)",
                    fontSize: 13, fontWeight: 600, cursor: "pointer",
                  }}
                >
                  Skip
                </button>
              </>
            ) : (
              <button
                onClick={hangup}
                title="Hang up"
                style={circleBtn("var(--crm-danger)", "#fff")}
              >
                <PhoneOff size={18} />
              </button>
            )}
          </div>
        </div>
      )}

      {showNotes && (
        <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 600, fontSize: 13 }}>
              <FileText size={14} style={{ color: "var(--crm-primary)" }} /> Call notes
            </div>
            <button
              onClick={() => setShowNotes(false)}
              aria-label="Close notes"
              style={{ border: "none", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", padding: 4 }}
            >
              <X size={14} />
            </button>
          </div>

          <div style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>
            Duration: {formatDuration(duration)} · {to}
          </div>

          <div>
            <label style={fieldLabel}>What was the call about? *</label>
            <textarea
              autoFocus
              rows={3}
              value={noteDescription}
              onChange={(e) => setNoteDescription(e.target.value)}
              placeholder="Key takeaways, next steps, action items…"
              style={{ ...inputBase, resize: "vertical", minHeight: 72 }}
            />
          </div>

          <div>
            <label style={fieldLabel}>Update title (optional)</label>
            <input
              type="text"
              value={noteTitle}
              onChange={(e) => setNoteTitle(e.target.value)}
              placeholder="e.g. Follow-up call — discussed pricing"
              style={inputBase}
            />
          </div>

          <div style={{ display: "flex", gap: 8, paddingTop: 2 }}>
            <button
              onClick={handleSaveNote}
              disabled={isSavingNote || !noteDescription.trim()}
              style={{
                flex: 1, height: 38, borderRadius: 8, border: "none",
                background: "var(--crm-primary)", color: "#fff",
                fontSize: 13, fontWeight: 600, cursor: "pointer",
                opacity: (isSavingNote || !noteDescription.trim()) ? 0.5 : 1,
                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}
            >
              {isSavingNote ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Save to timeline
            </button>
            <button
              onClick={onClose}
              disabled={isSavingNote}
              style={{
                height: 38, padding: "0 14px", borderRadius: 8, border: "none",
                background: "transparent", color: "var(--color-text-secondary)",
                fontSize: 13, fontWeight: 600, cursor: "pointer",
              }}
            >
              Skip
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
